import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { changePasswordFromLogin } from '../auth/api';
import './Login.css';

const SUPPORT_EMAIL = 'soporte@infova.com';

function isStrongPassword(value) {
  return value.length >= 8 && /[a-zA-Z]/.test(value) && /[0-9]/.test(value);
}

export default function Login() {
  const { login, isAuthenticated, loading } = useAuth();
  const [mode, setMode] = useState('login');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [changeIdentifier, setChangeIdentifier] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/" replace />;

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const showLogin = () => {
    resetMessages();
    setMode('login');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const showPasswordChange = () => {
    resetMessages();
    setChangeIdentifier(identifier.trim());
    setMode('change-password');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!identifier.trim() || !password.trim()) {
      setError('Introduce tu usuario y clave');
      return;
    }

    setSubmitting(true);
    try {
      await login(identifier.trim(), password);
    } catch (err) {
      setError(err.message || 'Error de conexion');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    resetMessages();

    const cleanIdentifier = changeIdentifier.trim();
    if (!cleanIdentifier || !currentPassword || !newPassword || !confirmPassword) {
      setError('Completa todos los campos');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('La nueva clave y su confirmacion no coinciden');
      return;
    }

    if (!isStrongPassword(newPassword)) {
      setError('La clave debe tener al menos 8 caracteres, con letras y numeros');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await changePasswordFromLogin(cleanIdentifier, currentPassword, newPassword);
      setIdentifier(cleanIdentifier);
      setPassword('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMode('login');
      setSuccess(response?.message || 'Clave actualizada correctamente. Ya puedes iniciar sesion.');
    } catch (err) {
      setError(err.message || 'Error de conexion');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <img
          className="login-logo"
          src={`${process.env.PUBLIC_URL}/images/logo_infova_black.svg`}
          alt="Logo"
        />
        <h1 className="login-title">Calculadora de Costes</h1>

        {mode === 'login' ? (
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-field">
              <label htmlFor="login-identifier">Usuario</label>
              <input
                id="login-identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Tu identificador"
                autoComplete="username"
                autoFocus
                disabled={submitting}
                aria-describedby={error ? 'login-error-msg' : success ? 'login-success-msg' : undefined}
              />
            </div>
            <div className="login-field">
              <label htmlFor="login-password">Clave</label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Tu clave"
                autoComplete="current-password"
                disabled={submitting}
                aria-describedby={error ? 'login-error-msg' : success ? 'login-success-msg' : undefined}
              />
            </div>
            {error && <p className="login-error" id="login-error-msg" role="alert">{error}</p>}
            {success && <p className="login-success" id="login-success-msg" role="status">{success}</p>}
            <button className="login-btn" type="submit" disabled={submitting}>
              {submitting ? 'Entrando...' : 'Entrar'}
            </button>
            <button className="login-link-btn" type="button" onClick={showPasswordChange} disabled={submitting}>
              Cambiar clave
            </button>
          </form>
        ) : (
          <form className="login-form" onSubmit={handlePasswordChange}>
            <div className="login-field">
              <label htmlFor="change-identifier">Usuario o email</label>
              <input
                id="change-identifier"
                type="text"
                value={changeIdentifier}
                onChange={(e) => setChangeIdentifier(e.target.value)}
                placeholder="Tu identificador o email"
                autoComplete="username"
                autoFocus
                disabled={changingPassword}
                aria-describedby={error ? 'change-error-msg' : undefined}
              />
            </div>
            <div className="login-field">
              <label htmlFor="current-password">Clave actual</label>
              <input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Clave actual"
                autoComplete="current-password"
                disabled={changingPassword}
                aria-describedby={error ? 'change-error-msg' : undefined}
              />
            </div>
            <div className="login-field">
              <label htmlFor="new-password">Nueva clave</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nueva clave"
                autoComplete="new-password"
                disabled={changingPassword}
                aria-describedby={error ? 'change-error-msg' : undefined}
              />
            </div>
            <div className="login-field">
              <label htmlFor="confirm-password">Confirmar nueva clave</label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la nueva clave"
                autoComplete="new-password"
                disabled={changingPassword}
                aria-describedby={error ? 'change-error-msg' : undefined}
              />
            </div>
            <p className="login-help">
              Usuario y clave son iguales por defecto. Si no recuerdas tu usuario, contacta con{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            </p>
            {error && <p className="login-error" id="change-error-msg" role="alert">{error}</p>}
            <button className="login-btn" type="submit" disabled={changingPassword}>
              {changingPassword ? 'Actualizando...' : 'Actualizar clave'}
            </button>
            <button className="login-link-btn" type="button" onClick={showLogin} disabled={changingPassword}>
              Volver al login
            </button>
          </form>
        )}


      </div>
    </div>
  );
}
